import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/lib/api';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refFromUrl = (sp.get('ref') || '').trim().toUpperCase().slice(0, 16);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user' });
  const [refCode, setRefCode] = useState(refFromUrl);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [newsletterOk, setNewsletterOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Persist referral in sessionStorage so Google OAuth round-trip keeps it
  useEffect(() => {
    if (refFromUrl) {
      sessionStorage.setItem('agn_ref_code', refFromUrl);
      setRefCode(refFromUrl);
    } else {
      const saved = sessionStorage.getItem('agn_ref_code');
      if (saved) setRefCode(saved);
    }
  }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ageOk) {
      const msg = 'Devi dichiarare di avere almeno 14 anni per registrarti';
      setErr(msg); toast.error(msg); return;
    }
    if (!privacyOk) {
      const msg = 'Devi accettare Termini e Privacy per registrarti';
      setErr(msg); toast.error(msg); return;
    }
    setBusy(true); setErr('');
    try {
      await register({ ...form, marketing_consent: newsletterOk,
                       age_confirmed: ageOk,
                       referral_code: refCode || undefined });
      sessionStorage.removeItem('agn_ref_code');
      if (newsletterOk) {
        toast.success('Benvenuto! Controlla la tua email per confermare la newsletter.');
      } else {
        toast.success('Benvenuto in AgriNexus');
      }
      nav('/dashboard');
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-16 items-center">
      <div className="hidden lg:block">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">Registrati</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-none">Entra nell’ecosistema.</h1>
        <p className="mt-6 text-muted-foreground max-w-md">La registrazione è gratuita. Scegli se cerchi un terreno o se hai un terreno da pubblicare — puoi cambiare in qualsiasi momento.</p>
      </div>
      <form onSubmit={submit} data-testid={AUTH.registerForm} className="rounded-2xl border border-border p-8 md:p-10 bg-card space-y-5 max-w-md w-full">
        <h2 className="font-display text-2xl">Crea il tuo account</h2>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid={AUTH.registerRoleUser} onClick={()=>setForm({...form, role:'user'})}
                  className={`rounded-xl border p-3 text-left text-sm transition ${form.role==='user'?'border-foreground bg-secondary':'border-border'}`}>
            <div className="font-medium">Sto cercando</div>
            <div className="text-xs text-muted-foreground">Un terreno / un’attività</div>
          </button>
          <button type="button" data-testid={AUTH.registerRoleOwner} onClick={()=>setForm({...form, role:'owner'})}
                  className={`rounded-xl border p-3 text-left text-sm transition ${form.role==='owner'?'border-foreground bg-secondary':'border-border'}`}>
            <div className="font-medium">Sono proprietario</div>
            <div className="text-xs text-muted-foreground">Ho un terreno da offrire</div>
          </button>
        </div>
        <div className="space-y-2"><Label>Nome</Label>
          <Input data-testid={AUTH.registerName} value={form.name} onChange={on('name')} required/></div>
        <div className="space-y-2"><Label>Email</Label>
          <Input type="email" data-testid={AUTH.registerEmail} value={form.email} onChange={on('email')} required/></div>
        <div className="space-y-2"><Label>Password</Label>
          <Input type="password" data-testid={AUTH.registerPassword} value={form.password} onChange={on('password')} required minLength={6}/></div>

        {refCode && (
          <div data-testid="register-ref-banner"
               className="rounded-xl border border-chartreuse/40 bg-chartreuse/10 p-3 flex items-center gap-3 text-sm">
            <Gift size={16} className="shrink-0 text-chartreuse-foreground" style={{ color: 'var(--chartreuse-foreground, #2a6207)' }}/>
            <div className="flex-1">
              Stai usando il codice invito <span className="font-mono font-bold">{refCode}</span>.
            </div>
            <button type="button" onClick={() => { setRefCode(''); sessionStorage.removeItem('agn_ref_code'); }}
                    className="text-xs text-muted-foreground hover:text-foreground underline">rimuovi</button>
          </div>
        )}

        <div className="space-y-3 rounded-xl border border-border p-4 bg-secondary/20">
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <input type="checkbox" data-testid="register-age-consent"
              checked={ageOk} onChange={e=>setAgeOk(e.target.checked)}
              className="mt-1 accent-chartreuse"/>
            <span className="text-muted-foreground leading-relaxed">
              Dichiaro di avere almeno 14 anni (richiesto per prestare il consenso al trattamento dati — GDPR Art. 8, D.Lgs. 101/2018). <span className="text-terracotta">*</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <input type="checkbox" data-testid="register-privacy-consent"
              checked={privacyOk} onChange={e=>setPrivacyOk(e.target.checked)}
              className="mt-1 accent-chartreuse"/>
            <span className="text-muted-foreground leading-relaxed">
              Ho letto e accetto i <Link to="/termini" target="_blank" className="underline text-foreground">Termini di servizio</Link> e l'<Link to="/privacy" target="_blank" className="underline text-foreground">Informativa privacy</Link>. <span className="text-terracotta">*</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <input type="checkbox" data-testid="register-newsletter-consent"
              checked={newsletterOk} onChange={e=>setNewsletterOk(e.target.checked)}
              className="mt-1 accent-chartreuse"/>
            <span className="text-muted-foreground leading-relaxed">
              Voglio ricevere newsletter, alert su nuovi terreni e comunicazioni promozionali. Ti invieremo un'email di conferma prima di aggiungerti alla lista. <span className="text-xs opacity-70">(facoltativo)</span>
            </span>
          </label>
        </div>

        {err && <div className="text-sm text-destructive">{err}</div>}
        <Button type="submit" disabled={busy} data-testid={AUTH.registerSubmit} className="w-full rounded-full h-11">
          {busy ? 'Creazione…' : 'Crea account'}
        </Button>
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border"/></div>
          <div className="relative flex justify-center text-xs uppercase tracking-widest"><span className="bg-card px-3 text-muted-foreground">oppure</span></div>
        </div>
        <Button type="button" variant="outline" onClick={googleLogin} className="w-full rounded-full h-11">
          Continua con Google
        </Button>
        <div className="text-sm text-center text-muted-foreground">
          Hai già un account? <Link to="/login" className="font-medium text-foreground link-underline">Accedi</Link>
        </div>
      </form>
    </div>
  );
}
